import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { HojaMenu } from '#components/mobile/ui/HojaMenu';
import { Icono } from '#components/mobile/ui/Icono';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, texto } from '#components/mobile/ui/tokens';

import { BocadilloGavi } from './BocadilloGavi';
import { columnasIniciales, nombresColumnas } from './columnas';
import { movimientosDeFilas } from './leerExtracto';
import type { Columnas, Lectura } from './leerExtracto';

type Campo = 'fecha' | 'concepto' | 'importe' | 'cargo' | 'abono';

type PasoColumnasProps = {
  filas: string[][];
  lectura: Lectura | null;
  onListo: (lectura: Lectura) => void;
};

/** «No, cambiar columnas»: elegir a mano qué columna es cada cosa. */
export function PasoColumnas({ filas, lectura, onListo }: PasoColumnasProps) {
  const { t } = useTranslation();
  const [columnas, setColumnas] = useState<Columnas>(
    () => lectura?.columnas ?? columnasIniciales(filas),
  );
  const [eligiendo, setEligiendo] = useState<Campo | null>(null);
  const filaCabecera = lectura?.filaCabecera ?? null;
  const nombres = nombresColumnas(filas, filaCabecera, t);
  const separados = columnas.importe == null;

  const nueva: Lectura = { filaCabecera, columnas };
  const { movimientos } = movimientosDeFilas(filas, nueva);

  const campos: Array<{ campo: Campo; texto: string }> = [
    { campo: 'fecha', texto: t('Date') },
    { campo: 'concepto', texto: t('Description') },
    ...(separados
      ? [
          { campo: 'cargo' as const, texto: t('Money out') },
          { campo: 'abono' as const, texto: t('Money in') },
        ]
      : [{ campo: 'importe' as const, texto: t('Amount') }]),
  ];

  const cambiarModo = (modo: 'uno' | 'dos') => {
    if (modo === 'dos' && !separados) {
      const base = columnas.importe ?? 0;
      setColumnas({
        ...columnas,
        importe: null,
        cargo: base,
        abono: Math.min(base + 1, nombres.length - 1),
      });
    } else if (modo === 'uno' && separados) {
      setColumnas({
        ...columnas,
        importe: columnas.cargo ?? 0,
        cargo: null,
        abono: null,
      });
    }
  };

  return (
    <View style={{ flex: 1, gap: 16, padding: espacio.margen }}>
      <BocadilloGavi estado="ladea">
        <Trans>Tell me which column is which and I'll try again.</Trans>
      </BocadilloGavi>

      <Segmentado
        etiqueta={t('How the amounts come')}
        testId="importar-modo"
        estirar
        valor={separados ? 'dos' : 'uno'}
        onChange={cambiarModo}
        opciones={[
          { valor: 'uno', texto: t('One amount') },
          { valor: 'dos', texto: t('Money out and in') },
        ]}
      />

      <Tarjeta
        relleno={0}
        style={{ overflow: 'hidden' }}
        data-testid="importar-columnas-tabla"
      >
        {campos.map(({ campo, texto: nombre }, i) => {
          const indice = columnas[campo];
          return (
            <Button
              key={campo}
              variant="bare"
              data-testid={`importar-columna-${campo}`}
              onPress={() => setEligiendo(campo)}
              style={{
                minHeight: 52,
                padding: '0 14px',
                justifyContent: 'space-between',
                borderTop: i === 0 ? 0 : `1px solid ${color.line}`,
                borderRadius: 0,
                gap: 10,
              }}
            >
              <Text style={texto.fila}>{nombre}</Text>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <Text
                  style={{
                    ...texto.cuerpo,
                    color: color.fg2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 190,
                  }}
                >
                  {indice != null ? nombres[indice] : '—'}
                </Text>
                <Icono nombre="down" size={16} style={{ color: color.fg2 }} />
              </View>
            </Button>
          );
        })}
      </Tarjeta>

      <Text
        style={{ ...texto.secundario, color: color.fg2, textAlign: 'center' }}
        data-testid="importar-columnas-cuenta"
      >
        {t('With these columns I find {{count}} transactions.', {
          count: movimientos.length,
        })}
      </Text>

      <View style={{ flex: 1 }} />

      <Boton
        bloque
        isDisabled={movimientos.length === 0}
        onPress={() => onListo(nueva)}
        data-testid="importar-columnas-listo"
      >
        <Trans>Use these columns</Trans>
      </Boton>

      <HojaMenu
        isOpen={eligiendo != null}
        titulo={campos.find(c => c.campo === eligiendo)?.texto ?? t('Column')}
        items={nombres.map((nombre, i) => ({ name: String(i), text: nombre }))}
        onMenuSelect={nombre => {
          if (eligiendo) {
            setColumnas({ ...columnas, [eligiendo]: Number(nombre) });
          }
          setEligiendo(null);
        }}
        onCerrar={() => setEligiendo(null)}
        data-testid="importar-elegir-columna"
      />
    </View>
  );
}
